import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import {
  Firestore,
  collection,
  query,
  where,
  getDocs,
  orderBy,
} from '@angular/fire/firestore';
import { AuthService } from 'src/app/auth.service';
import { ViewPublishedScheduleComponent } from '../view-published-schedule/view-published-schedule.component';

declare var bootstrap: any;

interface PublishedSchedule {
  id: string;
  weekStart: string;
  weekEnd: string;
  weekRange: string;
  publishedAt: any;
  publishedBy: string;
  result: any[];
  dates: { [key: string]: string };
}

@Component({
  selector: 'app-public-schedule',
  standalone: true,
  imports: [CommonModule, ViewPublishedScheduleComponent],
  templateUrl: './public-schedule.component.html',
  styleUrls: ['./public-schedule.component.css'],
})
export class PublicScheduleComponent implements OnInit {
  private readonly firestore = inject(Firestore);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);

  publishedSchedules: PublishedSchedule[] = [];
  isLoading = true;
  error: string | null = null;
  selectedSchedule: PublishedSchedule | null = null;
  employeeName: string | null = null;
  
  // Today properties
  todayShift: string | null = null;
  todayLabel: string = '';
  coworkersToday: string[] = [];
  isViewingToday = false;

  // Tomorrow properties
  tomorrowShift: string | null = null;
  tomorrowLabel: string = '';
  coworkersTomorrow: string[] = [];
  isViewingTomorrow = false;

  // Pagination properties
  currentPage = 1;
  itemsPerPage = 6;
  Math = Math;

  async ngOnInit() {
    this.employeeName = this.auth.getUserName();
    await this.loadPublishedSchedules();
  }

  // Get paginated schedules
  get paginatedSchedules(): PublishedSchedule[] {
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    return this.publishedSchedules.slice(startIndex, endIndex);
  }

  // Get total pages
  get totalPages(): number {
    return Math.ceil(this.publishedSchedules.length / this.itemsPerPage);
  }

  // Get page numbers for pagination
  get pageNumbers(): number[] {
    return Array.from({ length: this.totalPages }, (_, i) => i + 1);
  }

  // Navigate to specific page
  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  // Navigate to next page
  nextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  // Navigate to previous page
  previousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  getTodayKey(): string {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return days[new Date().getDay()];
  }

  getTomorrowKey(): string {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return days[tomorrow.getDay()];
  }

  getTodayData(schedule: any) {
    if (!schedule || !this.employeeName) return;

    const todayKey = this.getTodayKey();
    const result = schedule.result || [];

    const employee = result.find(
      (e: any) =>
        e.name?.trim().toLowerCase() === this.employeeName?.trim().toLowerCase()
    );

    const shift = employee?.[todayKey] || 'Off today';
    this.todayShift =
      shift.toLowerCase() === 'requested off'
        ? 'Requested Off'
        : shift || 'Off today';

    // ✅ Sort coworkers by earliest shift time, then alphabetically
    this.coworkersToday = result
      .filter(
        (e: any) =>
          e.name?.trim().toLowerCase() !==
            this.employeeName?.trim().toLowerCase() &&
          e[todayKey] &&
          e[todayKey].toLowerCase() !== 'off' &&
          e[todayKey].toLowerCase() !== 'requested off'
      )
      .map((e: any) => ({
        name: e.name,
        shift: e[todayKey],
        startTime: this.parseShiftStartTime(e[todayKey]),
      }))
      .sort((a: any, b: any) => {
        // Sort by start time first
        if (a.startTime !== null && b.startTime !== null) {
          if (a.startTime !== b.startTime) {
            return a.startTime - b.startTime;
          }
        }
        // If times are equal or null, sort alphabetically
        return a.name.localeCompare(b.name);
      })
      .map((e: any) => `${e.name} (${e.shift})`);

    const days = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ];
    this.todayLabel = days[new Date().getDay()];
  }

  getTomorrowData(schedule: any) {
    if (!schedule || !this.employeeName) return;

    const tomorrowKey = this.getTomorrowKey();
    const result = schedule.result || [];

    const employee = result.find(
      (e: any) =>
        e.name?.trim().toLowerCase() === this.employeeName?.trim().toLowerCase()
    );

    const shift = employee?.[tomorrowKey] || 'Off tomorrow';
    this.tomorrowShift =
      shift.toLowerCase() === 'requested off'
        ? 'Requested Off'
        : shift || 'Off tomorrow';

    // Sort coworkers by earliest shift time, then alphabetically
    this.coworkersTomorrow = result
      .filter(
        (e: any) =>
          e.name?.trim().toLowerCase() !==
            this.employeeName?.trim().toLowerCase() &&
          e[tomorrowKey] &&
          e[tomorrowKey].toLowerCase() !== 'off' &&
          e[tomorrowKey].toLowerCase() !== 'requested off'
      )
      .map((e: any) => ({
        name: e.name,
        shift: e[tomorrowKey],
        startTime: this.parseShiftStartTime(e[tomorrowKey]),
      }))
      .sort((a: any, b: any) => {
        // Sort by start time first
        if (a.startTime !== null && b.startTime !== null) {
          if (a.startTime !== b.startTime) {
            return a.startTime - b.startTime;
          }
        }
        // If times are equal or null, sort alphabetically
        return a.name.localeCompare(b.name);
      })
      .map((e: any) => `${e.name} (${e.shift})`);

    const days = [
      'Sunday',
      'Monday',
      'Tuesday',
      'Wednesday',
      'Thursday',
      'Friday',
      'Saturday',
    ];
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    this.tomorrowLabel = days[tomorrow.getDay()];
  }

  // ✅ Parse shift string to get start time in minutes from midnight
  private parseShiftStartTime(shift: string): number | null {
    if (!shift || shift === 'off') return null;

    // Match patterns like "9am-5pm", "9:00am-5:00pm", "9-5", "9:00 AM-5:00 PM", etc.
    const timeMatch = shift.match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/i);
    if (!timeMatch) return null;

    let hours = parseInt(timeMatch[1], 10);
    const minutes = timeMatch[2] ? parseInt(timeMatch[2], 10) : 0;
    const meridiem = timeMatch[3]?.toLowerCase();

    // Handle 12-hour format
    if (meridiem === 'pm' && hours !== 12) {
      hours += 12;
    } else if (meridiem === 'am' && hours === 12) {
      hours = 0;
    } else if (!meridiem && hours < 12) {
      // If no am/pm specified and hour is less than 12, assume it might need adjustment
      // This handles cases like "1:00-9:30" where 1:00 is likely 1:00 PM
      // You may want to adjust this logic based on your business rules
    }

    // Convert to minutes from midnight
    return hours * 60 + minutes;
  }

  openTodayScheduleModal() {
    const current = this.getCurrentWeekSchedule();
    if (!current) return;

    this.isViewingToday = true;
    this.isViewingTomorrow = false;
    this.selectedSchedule = current;
    this.getTodayData(current);

    const modalEl = document.getElementById('viewScheduleModal');
    if (modalEl) new bootstrap.Modal(modalEl).show();
  }

  openTomorrowScheduleModal() {
    const schedule = this.getTomorrowWeekSchedule();
    if (!schedule) return;

    this.isViewingTomorrow = true;
    this.isViewingToday = false;
    this.selectedSchedule = schedule;
    this.getTomorrowData(schedule);

    const modalEl = document.getElementById('viewScheduleModal');
    if (modalEl) new bootstrap.Modal(modalEl).show();
  }

  private async loadPublishedSchedules() {
    const orgId = this.auth.getUsersOrgId();
    if (!orgId) {
      this.error = 'No organization ID found';
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    try {
      const schedulesRef = collection(this.firestore, 'schedules');
      const q = query(
        schedulesRef,
        where('orgId', '==', orgId),
        where('status', '==', 'published'),
        orderBy('weekStart', 'desc')
      );

      const querySnapshot = await getDocs(q);

      this.publishedSchedules = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          weekStart: data['weekStart'],
          weekEnd: data['weekEnd'],
          weekRange: data['weekRange'],
          publishedAt: data['publishedAt'],
          publishedBy: data['publishedBy'],
          result: data['result'] || [],
          dates: data['dates'] || {},
        } as PublishedSchedule;
      });
    } catch (err) {
      console.error('Error loading published schedules:', err);
      this.error = 'Failed to load schedules. Please try again.';
    } finally {
      this.isLoading = false;
    }
  }

  formatWeekLabel(schedule: PublishedSchedule): string {
    if (!schedule.weekStart || !schedule.weekEnd) return 'Unknown Week';

    const startDate = new Date(schedule.weekStart + 'T00:00:00');
    const endDate = new Date(schedule.weekEnd + 'T00:00:00');

    const options: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    };

    const start = startDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
    const end = endDate.toLocaleDateString('en-US', options);

    return `${start} - ${end}`;
  }

  openScheduleModal(schedule: PublishedSchedule) {
    this.isViewingToday = false;
    this.isViewingTomorrow = false;
    this.selectedSchedule = schedule;
    const modalEl = document.getElementById('viewScheduleModal');
    if (modalEl) {
      const modal = new bootstrap.Modal(modalEl);
      modal.show();
    }
  }

  isCurrentWeek(schedule: PublishedSchedule): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const start = new Date(schedule.weekStart + 'T00:00:00');
    const end = new Date(schedule.weekEnd + 'T00:00:00');

    return today >= start && today <= end;
  }

  getCurrentWeekSchedule(): PublishedSchedule | null {
    return this.publishedSchedules.find((s) => this.isCurrentWeek(s)) || null;
  }

  getTomorrowWeekSchedule(): PublishedSchedule | null {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);

    return (
      this.publishedSchedules.find((s) => {
        const start = new Date(s.weekStart + 'T00:00:00');
        const end = new Date(s.weekEnd + 'T00:00:00');
        return tomorrow >= start && tomorrow <= end;
      }) || null
    );
  }
}